import { getCurrentOperatorName } from '../../utils/currentOperator';
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import BaseTicketScreen from './BaseTicketScreen';
import { FormRow, FormGroup, Label, Input, Select, FieldError } from './shared/TicketStyledComponents';
import SecurityPicker, { PickedSecurity } from './shared/SecurityPicker';
import {
  BandEyebrow,
  BandHeader,
  BandSubline,
  BandTitle,
  BandTitleGroup,
  DockTitle,
  EconomicsPreview,
  InstrumentGrid,
  ScopePill,
  SearchDock,
  StructuredBand,
  StructuredContextPanel,
  StructuredHeroPanel,
  StructuredWorkspace,
  TradeEconomicsGrid,
  type StructuredContextRow,
} from './shared/StructuredCreditTicketLayout';
import { getTodayDate } from '../../utils/dateUtils';
import { calendarsFor } from '../../utils/businessDate';
import { formatAmountWithCommas, parseAmountShorthand } from '../../utils/amountUtils';
import { BadgeDollarSign, CalendarDays, Landmark, Layers, Search, ShieldCheck, Split, Users } from 'lucide-react';

import { DateField } from '../shared/DateField';

const fmtAmount = (value: string) => {
  const parsed = parseAmountShorthand(value);
  return parsed > 0 ? formatAmountWithCommas(parsed) : value;
};

const num = (value: any, fallback = 0) => {
  const n = value === '' || value == null ? fallback : parseAmountShorthand(String(value));
  return Number.isFinite(n) ? n : fallback;
};

const rowsFrom = (rows: Array<[string, any, boolean?]>): StructuredContextRow[] =>
  rows
    .filter(([, value]) => value != null && value !== '')
    .map(([label, value, accent]) => ({ label, value: String(value), accent }));

const CloDealContext: React.FC<{ securityId?: number | null }> = ({ securityId }) => {
  const [detail, setDetail] = useState<any | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (!securityId) {
      setDetail(null);
      return;
    }
    (async () => {
      try {
        const { data } = await axios.get(`/api/credit/clo-equity/securities/${securityId}/deal-detail`);
        if (!cancelled) setDetail(data?.found ? data : null);
      } catch {
        if (!cancelled) setDetail(null);
      }
    })();
    return () => { cancelled = true; };
  }, [securityId]);

  const rows = detail ? rowsFrom([
    ['Deal', detail.dealName, true],
    ['Manager', detail.managerName],
    ['Deal Type', detail.dealType],
    ['Tranche', detail.trancheName, true],
    ['WARF', detail.warf],
    ['WAL', detail.wal != null ? `${detail.wal} yrs` : null],
    ['Diversity', detail.diversityScore],
    ['Spread', detail.spreadBps != null ? `${detail.spreadBps} bps` : null, true],
    ["Moody's", detail.moodysRating],
    ['S&P', detail.spRating],
    ['Fitch', detail.fitchRating],
  ]) : [];

  return (
    <StructuredContextPanel
      toneName="clo"
      icon={Layers}
      title="Deal And Tranche Snapshot"
      subtitle={detail?.dealName || 'CLO collateral and tranche profile'}
      rows={rows}
      emptyTitle={securityId ? 'Deal detail unavailable' : 'No tranche selected'}
      emptyText={securityId ? 'The selected security has no CLO deal snapshot.' : 'Select a tranche to show manager, tranche, WARF, WAL, spread, and ratings.'}
    />
  );
};

const CLOEquityTicketScreen: React.FC = () => {
  const navigate = useNavigate();
  // ADR 0009: a CLO tranche trades on SIFMA (its currency's calendar outside dollars). The ticket
  // settles on the trade date (T+0), which the trade date field keeps on a business day.
  const openTradeDate = getTodayDate(calendarsFor('CLO_TRANCHE', 'USD'));
  const initialFormData = {
    transactionType: 'BUY' as const,
    assetManager: '',
    portfolioManager: '',
    assetClass: 'CLO_EQUITY',
    assetSubClass: 'CLO Equity',
    portfolio: '',
    tradeDate: openTradeDate,
    settleDate: openTradeDate,
    submittedBy: getCurrentOperatorName(),
    currency: 'USD',
    executedBy: '',
    notes: '',
    securityId: '' as any,
    cusip: '',
    security: '',
    investmentAmount: '',
    price: '100',
  };

  const validateForm = (formData: any, action: string): { [key: string]: string } => {
    const errors: { [key: string]: string } = {};
    const isCreate = action === 'draft' || action === 'submitForPreClear';
    if (isCreate) {
      if (!formData.securityId) errors.securityId = 'Select a CLO tranche';
      if (!formData.portfolio?.trim()) errors.portfolio = 'Portfolio is required';
      if (num(formData.investmentAmount) <= 0) errors.investmentAmount = 'Investment amount must be > 0';
      if (num(formData.price) <= 0) errors.price = 'Price must be > 0';
      if (!formData.tradeDate) errors.tradeDate = 'Trade date is required';
    }
    if (action === 'execute' && !formData.executedBy?.trim()) errors.executedBy = 'Executed By is required';
    return errors;
  };

  const loadTransactionData = (transaction: any) => {
    const str = (v: any) => (v == null ? '' : v.toString());
    return {
      ...initialFormData,
      transactionType: transaction.transactionType || 'BUY',
      assetManager: transaction.assetManagerName || transaction.assetManager || '',
      portfolioManager: transaction.portfolioManagerName || transaction.portfolioManager || '',
      portfolio: transaction.portfolioName || transaction.portfolio || '',
      securityId: transaction.securityId || '',
      cusip: transaction.cusip || '',
      security: transaction.securityName || transaction.cusip || '',
      investmentAmount: str(transaction.quantity != null ? transaction.quantity : transaction.amount),
      price: transaction.price != null ? transaction.price.toString() : '100',
      tradeDate: transaction.tradeDate || getTodayDate(),
      settleDate: transaction.settleDate || getTodayDate(),
      submittedBy: transaction.createdBy || getCurrentOperatorName(),
      currency: transaction.currency || 'USD',
      executedBy: transaction.executedBy || '',
      notes: transaction.notes || '',
    };
  };

  const buildTransactionData = async (formData: any, dimensionData: any): Promise<any> => {
    const portfolio = dimensionData.portfolios.find((p: any) => p.portfolioName === formData.portfolio.trim());
    if (!portfolio) throw new Error(`Portfolio "${formData.portfolio}" not found`);
    if (!formData.securityId) throw new Error('No CLO tranche selected');
    let assetManagerId: number | null = null;
    if (formData.assetManager?.trim()) {
      const am = dimensionData.assetManagers.find((a: any) => a.assetManagerName === formData.assetManager.trim());
      if (am?.id) assetManagerId = am.id;
    }
    let portfolioManagerId: number | null = null;
    if (formData.portfolioManager?.trim()) {
      const pm = dimensionData.portfolioManagers.find((p: any) => p.portfolioManagerName === formData.portfolioManager.trim());
      if (pm?.id) portfolioManagerId = pm.id;
    }
    const investmentAmount = num(formData.investmentAmount);
    const price = num(formData.price, 100);
    // V342 deal metadata: lets CLOPostingSpi find-or-create the silo + attrs
    // at POST. Best-effort — an unlinked security books without deal context
    // (disclosed) rather than failing.
    let cloDealName: string | null = null;
    let cloManagerName: string | null = null;
    try {
      const { data } = await axios.get(`/api/credit/clo-equity/securities/${formData.securityId}/deal-detail`);
      if (data?.found) {
        cloDealName = data.dealName || null;
        cloManagerName = data.managerName || null;
      }
    } catch { /* book without deal context */ }
    return {
      cloDealName,
      cloManagerName,
      transactionType: 'BUY',
      securityId: Number(formData.securityId),
      portfolioId: portfolio.id,
      assetManagerId,
      portfolioManagerId,
      // F-CLO-020 (CLO E2E 2026-08-29): for par-quantity tickets the server
      // treats `amount` as FACE and derives principal = face x price itself.
      // Sending investment x price here made the price apply TWICE (a 5M-face
      // @96 order booked 4.8M face at 96^2 cost).
      quantity: investmentAmount,
      price,
      amount: investmentAmount,
      tradeDate: formData.tradeDate,
      settleDate: formData.tradeDate,
      notes: formData.notes || `CLO tranche acquisition: ${formData.security || formData.cusip}`,
      createdBy: formData.submittedBy,
      executedBy: formData.executedBy || null,
      status: 'DRAFT',
      ticketType: 'CLO_TRANCHE',
      eventType: 'ACQUIRE',
      // F-CLO-028: unique per-booking suffix — a second (add-on) acquisition of
      // the same tranche is legitimate and must not collide with the first on
      // the idempotency index. Lifecycle lookups match on the prefix.
      sourceRef: `clo-equity:sec-${formData.securityId}:acquire:${Date.now()}`,
      currency: formData.currency || 'USD',
      cusip: formData.cusip,
    };
  };

  return (
    <BaseTicketScreen
      title="CLO Tranche Trade Entry"
      assetClass="CLO_EQUITY"
      assetSubClass="CLO Tranche"
      hidePortfolioField
      initialFormData={initialFormData}
      validateForm={validateForm}
      buildTransactionData={buildTransactionData}
      loadTransactionData={loadTransactionData}
    >
      {({ formData, setFormData, errors, dimensionData, transactionStatus, isFieldDisabled, handleInputChange }) => {
        const pickerLocked = isFieldDisabled('securityId') || (!!transactionStatus && transactionStatus !== 'DRAFT');
        const selected: PickedSecurity | null = formData.securityId
          ? { id: Number(formData.securityId), cusip: formData.cusip, securityName: formData.security }
          : null;
        const investmentAmount = num(formData.investmentAmount);
        const price = num(formData.price, 100);
        const consideration = investmentAmount * price / 100;

        return (
          <StructuredWorkspace>
            <StructuredHeroPanel
              toneName="clo"
              icon={Split}
              eyebrow="Structured Credit"
              title="CLO Tranche"
              description="CLO acquisition ticket with manager, tranche, WARF, WAL, spread, and ratings context next to commitment economics."
              badges={['CLO', 'Investment x price', 'IBOR posted']}
              signals={[
                { label: 'Selected', value: formData.security || 'No tranche', accent: !!formData.security },
                { label: 'CUSIP', value: formData.cusip || '-' },
                { label: 'Settlement', value: 'Trade date' },
                { label: 'Currency', value: formData.currency || 'USD' },
              ]}
            />

            <StructuredBand>
              <BandHeader>
                <BandTitleGroup>
                  <BandEyebrow $tone="clo"><Search size={14} /> Instrument</BandEyebrow>
                  <BandTitle>Tranche Selection</BandTitle>
                  <BandSubline>Security master selection with read-only CLO deal context.</BandSubline>
                </BandTitleGroup>
                <ScopePill $tone="clo">Senior, Mezz, Equity</ScopePill>
              </BandHeader>
              <InstrumentGrid>
                <SearchDock $tone="clo">
                  <DockTitle><ShieldCheck size={16} /> Approved Instrument</DockTitle>
                  <SecurityPicker
                    assetClass="CLO_SENIOR,CLO_MEZZ,CLO_EQUITY"
                    label="CLO Tranche (CUSIP)*"
                    placeholder="Search by deal, tranche, or CUSIP..."
                    selected={selected}
                    disabled={pickerLocked}
                    error={errors.securityId}
                    notFoundLabel="Define new CLO deal/tranche"
                    onDefineNew={() => navigate('/reference-data?tab=structured&assetClass=CLO&new=1')}
                    onSelect={(sec) => setFormData((prev: any) => ({
                      ...prev,
                      securityId: sec.id,
                      cusip: sec.cusip || '',
                      security: sec.securityName || sec.cusip || '',
                      currency: sec.currency || prev.currency,
                    }))}
                    onClear={() => setFormData((prev: any) => ({ ...prev, securityId: '', cusip: '', security: '' }))}
                  />
                </SearchDock>
                <CloDealContext securityId={formData.securityId ? Number(formData.securityId) : null} />
              </InstrumentGrid>
            </StructuredBand>

            <TradeEconomicsGrid>
              <StructuredBand>
                <BandHeader>
                  <BandTitleGroup>
                    <BandEyebrow $tone="clo"><CalendarDays size={14} /> Trade Setup</BandEyebrow>
                    <BandTitle>Portfolio, Trade Date, And Price</BandTitle>
                    <BandSubline>Drafted as an acquisition trade and posted against the selected CLO tranche.</BandSubline>
                  </BandTitleGroup>
                  <ScopePill $tone="clo">BUY</ScopePill>
                </BandHeader>

                <FormRow>
                  <FormGroup>
                    <Label>Portfolio*</Label>
                    <Select value={formData.portfolio} onChange={(e) => handleInputChange('portfolio', e.target.value)} className={errors.portfolio ? 'error' : ''} disabled={isFieldDisabled('portfolio')}>
                      <option value="">Select Portfolio</option>
                      {dimensionData.portfolios.map((p: any) => (<option key={p.id} value={p.portfolioName}>{p.portfolioName}</option>))}
                    </Select>
                    {errors.portfolio && <FieldError>{errors.portfolio}</FieldError>}
                  </FormGroup>
                  <FormGroup>
                    <Label><Landmark size={13} /> Asset Manager</Label>
                    <Select value={formData.assetManager} onChange={(e) => handleInputChange('assetManager', e.target.value)} disabled={isFieldDisabled('assetManager')}>
                      <option value="">Select</option>
                      {dimensionData.assetManagers.map((am: any) => (<option key={am.id} value={am.assetManagerName}>{am.assetManagerName}</option>))}
                    </Select>
                  </FormGroup>
                  <FormGroup>
                    <Label><Users size={13} /> Portfolio Manager</Label>
                    <Select value={formData.portfolioManager} onChange={(e) => handleInputChange('portfolioManager', e.target.value)} disabled={isFieldDisabled('portfolioManager')}>
                      <option value="">Select</option>
                      {dimensionData.portfolioManagers.map((pm: any) => (<option key={pm.id} value={pm.portfolioManagerName}>{pm.portfolioManagerName}</option>))}
                    </Select>
                  </FormGroup>
                </FormRow>

                <FormRow>
                  <FormGroup>
                    <Label>Trade Date*</Label>
                    <DateField value={formData.tradeDate ?? ''} className={errors.tradeDate ? 'error' : ''} disabled={isFieldDisabled('tradeDate')} calendars={calendarsFor('CLO_TRANCHE', formData.currency)} onChange={iso => handleInputChange('tradeDate', iso)} />
                    {errors.tradeDate && <FieldError>{errors.tradeDate}</FieldError>}
                  </FormGroup>
                  <FormGroup>
                    <Label>Executed By{transactionStatus === 'SENT' ? '*' : ''}</Label>
                    <Select value={formData.executedBy} onChange={(e) => handleInputChange('executedBy', e.target.value)} disabled={isFieldDisabled('executedBy')} className={errors.executedBy ? 'error' : ''}>
                      <option value="">Select</option>
                      {dimensionData.users.map((u: any) => (<option key={u.id} value={u.fullName}>{u.fullName}{u.role ? ` (${u.role})` : ''}</option>))}
                    </Select>
                    {errors.executedBy && <FieldError>{errors.executedBy}</FieldError>}
                  </FormGroup>
                </FormRow>

                <FormRow>
                  <FormGroup>
                    <Label><BadgeDollarSign size={13} /> Investment Amount*</Label>
                    <Input value={formData.investmentAmount} onChange={(e) => handleInputChange('investmentAmount', e.target.value)} onBlur={(e) => setFormData((p: any) => ({ ...p, investmentAmount: fmtAmount(e.target.value) }))} className={errors.investmentAmount ? 'error' : ''} disabled={isFieldDisabled('investmentAmount')} />
                    {errors.investmentAmount && <FieldError>{errors.investmentAmount}</FieldError>}
                  </FormGroup>
                  <FormGroup>
                    <Label>Price (% of par)*</Label>
                    <Input type="number" step="0.01" value={formData.price} onChange={(e) => handleInputChange('price', e.target.value)} className={errors.price ? 'error' : ''} disabled={isFieldDisabled('price')} />
                    {errors.price && <FieldError>{errors.price}</FieldError>}
                  </FormGroup>
                </FormRow>
              </StructuredBand>

              <EconomicsPreview
                toneName="clo"
                title="Live Economics"
                notionalLabel="Investment Amount"
                notionalValue={investmentAmount}
                price={price}
                consideration={consideration}
                currency={formData.currency || 'USD'}
                extraMetrics={[
                  { label: 'Settlement', value: formData.tradeDate || '-' },
                  { label: 'Posting', value: 'Tranche position', accent: true },
                ]}
              />
            </TradeEconomicsGrid>
          </StructuredWorkspace>
        );
      }}
    </BaseTicketScreen>
  );
};

export default CLOEquityTicketScreen;
